import type { CSSProperties, FunctionComponent } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import Heading from '@atlaskit/heading';
import Spinner from '@atlaskit/spinner';

import PermissionsBullets from './PermissionsBullets';
import SiteSelector from './SiteSelector/SiteSelector';
import {
  useInfo,
  useSelectedOrg,
  useSelectedWorkspace,
} from '../../Flow/ConsentContextProvider';
import TwgConsentBanners from './TwgConsentBanners';
import TwgSiteAccessNotice from './TwgSiteAccessNotice';
import TwgEmptyState from './TwgEmptyState';

const messages = defineMessages({
  permissionsHeading: {
    id: 'oauth.twg.permissions.sectionTitle',
    defaultMessage: 'Permissions',
    description: 'Section heading for TWG permissions',
  },
});

const styles: Record<string, CSSProperties> = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '36px',
    width: '100%',
  },
  loading: {
    paddingBlockStart: '4px',
  },
  permissionsRegion: {
    display: 'flex',
    flexDirection: 'column',
    gap: '40px',
  },
};

interface TWGProps {
  defaultPermissionsEditMode?: boolean;
  permissionsEditMode?: boolean;
  onPermissionsEditModeChange?: (editMode: boolean) => void;
}

const TWG: FunctionComponent<TWGProps> = ({
  defaultPermissionsEditMode = false,
  permissionsEditMode,
  onPermissionsEditModeChange,
}) => {
  const { availableOrganizations = [] } = useInfo();
  const { selectedOrg, isOrgLoading, orgError } = useSelectedOrg();
  const { selectedWorkspace } = useSelectedWorkspace();
  const { formatMessage } = useIntl();
  const hasSites = availableOrganizations.some(
    (org) => (org.availableWorkspaces ?? []).length > 0,
  );
  const orgSelected = !!selectedOrg && !isOrgLoading && !orgError;
  const permissionsVisible = orgSelected && !!selectedWorkspace;
  const errorVariant =
    orgError?.error === 'no-scopes' ? 'blocked' : orgError ? 'network' : null;

  if (!hasSites) {
    return (
      <div style={styles.root}>
        <TwgEmptyState variant="no-sites" />
      </div>
    );
  }

  return (
    <div style={styles.root}>
      {!errorVariant && <TwgConsentBanners />}
      <SiteSelector />
      {!errorVariant && isOrgLoading && (
        <div style={styles.loading}>
          <Spinner size="medium" label="Loading permissions" delay={0} />
        </div>
      )}
      {errorVariant && (
        <>
          <Heading size="small">
            {formatMessage(messages.permissionsHeading)}
          </Heading>
          <TwgEmptyState variant={errorVariant} />
        </>
      )}
      {!errorVariant && (
        /* Keep PermissionsBullets mounted to preserve expand state; hide it until site policy is resolved */
        <div
          style={{
            ...styles.permissionsRegion,
            display: permissionsVisible ? 'flex' : 'none',
          }}
        >
          {permissionsVisible && <TwgSiteAccessNotice />}
          <PermissionsBullets
            defaultEditMode={defaultPermissionsEditMode}
            editMode={permissionsEditMode}
            onEditModeChange={onPermissionsEditModeChange}
          />
        </div>
      )}
    </div>
  );
};

export default TWG;
